import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Package, Truck, CheckCircle, Clock, X, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  created: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-blue-500/20 text-blue-400', icon: Package },
  dispatched: { label: 'Dispatched', color: 'bg-purple-500/20 text-purple-400', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-600/20 text-green-500', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: X }
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/orders`, {
        withCredentials: true
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const filterOrders = useCallback(() => {
    let filtered = orders;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.order_id?.toLowerCase().includes(term) ||
        order.user?.name?.toLowerCase().includes(term) ||
        order.user?.email?.toLowerCase().includes(term) ||
        order.shipping_name?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    
    setFilteredOrders(filtered);
  }, [orders, searchTerm, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    filterOrders();
  }, [filterOrders]);

  const handleUpdateStatus = async (orderId, newStatus, trackingNumber = null) => {
    setUpdating(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/orders/${orderId}/status`,
        { 
          status: newStatus,
          tracking_number: trackingNumber,
          dispatch_date: newStatus === 'dispatched' ? new Date().toISOString() : null,
          delivery_date: newStatus === 'delivered' ? new Date().toISOString() : null
        },
        { withCredentials: true }
      );
      
      setOrders(orders.map(order => 
        order.order_id === orderId ? { ...order, status: newStatus, tracking_number: trackingNumber || order.tracking_number } : order
      ));
      
      toast.success(`Order status updated to ${newStatus}`);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Failed to update order status:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-orders">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#0A0A0A]">Order Management</h1>
        <p className="text-[#525252] mt-1">Track and manage all orders</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#525252]" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by order ID, customer name, or email..."
            className="pl-10 bg-white border-[#0A0A0A]/10 text-[#0A0A0A]"
            data-testid="order-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-[#0A0A0A]/10 text-[#0A0A0A] rounded-lg px-4 py-2"
          data-testid="order-status-filter"
        >
          <option value="all">All Orders</option>
          <option value="created">Pending</option>
          <option value="paid">Paid</option>
          <option value="processing">Processing</option>
          <option value="dispatched">Dispatched</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-[#0A0A0A]/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F1EA]">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Order ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Customer</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Type</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Amount</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0A0A0A]/10">
              {filteredOrders.map((order) => {
                const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.created;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <tr key={order.order_id} className="hover:bg-[#EBE6DC]/50" data-testid={`order-row-${order.order_id}`}>
                    <td className="px-6 py-4">
                      <span className="text-[#0A0A0A] font-mono text-sm">{order.order_id}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-[#0A0A0A] font-medium">{order.user?.name || order.shipping_name}</p>
                        <p className="text-sm text-[#525252]">{order.user?.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[#525252] capitalize">{order.order_type?.replace('_', ' ')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[#0A0A0A] font-medium">₹{order.amount}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#525252]">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => setSelectedOrder(order)}
                          variant="ghost"
                          size="sm"
                          className="text-[#B8935A] hover:text-[#B8935A] hover:bg-[#B8935A]/10"
                          data-testid={`manage-order-${order.order_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Manage
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#525252]">No orders found</p>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-[#525252]">
        Showing {filteredOrders.length} of {orders.length} orders
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[#0A0A0A]/10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#0A0A0A]">Order Details</h2>
                <button onClick={() => setSelectedOrder(null)} className="text-[#525252] hover:text-[#0A0A0A]">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#525252]">Order ID</p>
                  <p className="text-[#0A0A0A] font-mono">{selectedOrder.order_id}</p>
                </div>
                <div>
                  <p className="text-sm text-[#525252]">Amount</p>
                  <p className="text-[#0A0A0A] font-semibold">₹{selectedOrder.amount}</p>
                </div>
                <div>
                  <p className="text-sm text-[#525252]">Order Type</p>
                  <p className="text-[#0A0A0A] capitalize">{selectedOrder.order_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-[#525252]">Current Status</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedOrder.status]?.color}`}>
                    {STATUS_CONFIG[selectedOrder.status]?.label}
                  </span>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-3">Customer Information</h3>
                <div className="bg-[#F5F1EA] rounded-lg p-4 space-y-2">
                  <p className="text-[#0A0A0A]">{selectedOrder.shipping_name}</p>
                  <p className="text-[#525252]">{selectedOrder.shipping_phone}</p>
                  <p className="text-[#525252]">
                    {selectedOrder.shipping_address}, {selectedOrder.shipping_city}, 
                    {selectedOrder.shipping_state} - {selectedOrder.shipping_pincode}
                  </p>
                </div>
              </div>

              {/* Tracking Info */}
              {selectedOrder.tracking_number && (
                <div>
                  <p className="text-sm text-[#525252]">Tracking Number</p>
                  <p className="text-[#0A0A0A] font-mono">{selectedOrder.tracking_number}</p>
                </div>
              )}

              {/* Status Update */}
              <div>
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-3">Update Status</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {['paid', 'processing', 'dispatched', 'delivered', 'cancelled'].map((status) => {
                    const config = STATUS_CONFIG[status];
                    return (
                      <Button
                        key={status}
                        onClick={() => {
                          if (status === 'dispatched') {
                            const trackingNumber = prompt('Enter tracking number (optional):');
                            handleUpdateStatus(selectedOrder.order_id, status, trackingNumber);
                          } else {
                            handleUpdateStatus(selectedOrder.order_id, status);
                          }
                        }}
                        disabled={updating || selectedOrder.status === status}
                        variant="outline"
                        className={`border-[#0A0A0A]/15 ${selectedOrder.status === status ? 'bg-white' : 'hover:bg-[#EBE6DC]'}`}
                        data-testid={`set-status-${status}`}
                      >
                        {config.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
